/// <reference lib="dom" />
import { useEffect, useRef, useState } from "hono/jsx";
import { generateFarmName } from "@/features/rooms/lib/nameGenerator.ts";
import { RoomState } from "../server/types.ts";
import { QuizSidebar } from "./QuizSidebar.tsx";
import { QuizPlayerMenu } from "./QuizPlayerMenu.tsx";
import { QuizViewNotFound } from "./views/QuizViewNotFound.tsx";
import { QuizViewLocked } from "./views/QuizViewLocked.tsx";
import { QuizViewBanned } from "./views/QuizViewBanned.tsx";
import { QuizQuitModal } from "./QuizQuitModal.tsx";

export const QuizRoom = (
  { roomId, user }: {
    roomId: string;
    user?: { id: string; name?: string; isAnonymous?: boolean };
  },
) => {
  const [connected, setConnected] = useState(false);

  // Consolidate room state into one object to match server updates
  const [roomState, setRoomState] = useState<RoomState | null>(null);

  // Local UI state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [showQuitDialog, setShowQuitDialog] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [availableSets, setAvailableSets] = useState<
    { my: any[]; public: any[] }
  >({ my: [], public: [] });

  // Auth/Identity
  const [myId, setMyId] = useState<string | null>(user?.id || null);
  const [myName, setMyName] = useState(user?.name || "");
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState("");

  // Input State
  const [localAnswer, setLocalAnswer] = useState("");

  // Errors & Views
  const [roomNotFound, setRoomNotFound] = useState<
    boolean | { found: false; isReserved?: boolean; isOwner?: boolean }
  >(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [inputPassword, setInputPassword] = useState("");
  const [password, setPassword] = useState<string>(() => {
    if (typeof globalThis.window !== "undefined") {
      const urlParams = new URLSearchParams(globalThis.location.search);
      const urlPw = urlParams.get("password");
      if (urlPw) return urlPw;
      return localStorage.getItem(`quiz_pw_${roomId}`) || "";
    }
    return "";
  });
  const [banMessage, setBanMessage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Menus
  const [contextMenu, setContextMenu] = useState<
    { x: number; y: number; playerId: string } | null
  >(null);
  const [confirmModal, setConfirmModal] = useState<
    {
      isOpen: boolean;
      title: string;
      message: string;
      onConfirm: () => void;
    } | null
  >(null);

  const wsRef = useRef<WebSocket | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Global click to close contest menu (though handled in menu component too)
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    globalThis.window?.addEventListener("click", handleClick);
    return () => globalThis.window?.removeEventListener("click", handleClick);
  }, []);

  // Initialize random name if not logged in
  useEffect(() => {
    // If user has a name, use it as default
    if (user?.name) {
      setMyName(user.name);
      setTempName(user.name);
    } else if (!myName) {
      const stored = localStorage.getItem("quiz_name");
      if (stored) {
        setMyName(stored);
        setTempName(stored);
      } else {
        const newName = generateFarmName();
        setMyName(newName);
        setTempName(newName);
        localStorage.setItem("quiz_name", newName);
      }
    }
  }, [user]);

  // Sync input password if error
  useEffect(() => {
    if (passwordError && password) {
      setInputPassword(password);
    }
  }, [passwordError, password]);

  // [Refinement] Clear input when question changes
  useEffect(() => {
    setLocalAnswer("");
  }, [roomState?.currentQuestion]);

  useEffect(() => {
    if (!roomId) return;
    if (typeof window === "undefined") return;

    const protocol = globalThis.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl =
      `${protocol}//${globalThis.location.host}/ws/quiz?room=${roomId}&playerId=${
        myId || ""
      }${myName ? `&name=${encodeURIComponent(myName)}` : ""}&password=${
        encodeURIComponent(password)
      }`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("WS Connected");
      setConnected(true);
      setBanMessage(null);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "STATE_UPDATE") {
          setRoomState(data);
          setRoomNotFound(false);
          setPasswordError(null);

          if (data.playerId && data.playerId !== myId) {
            setMyId(data.playerId);
            // If we generated a random ID locally (though we don't persist it for quiz yet like geo), we updates it
          }
        } else if (data.type === "ROOM_NOT_FOUND") {
          setRoomNotFound({
            found: false,
            isReserved: data.isReserved,
            isOwner: data.isOwner,
          });
        } else if (data.type === "ERROR") {
          if (data.message === "Incorrect password") {
            setPasswordError("Incorrect password");
            localStorage.removeItem(`quiz_pw_${roomId}`);
          }
        } else if (data.type === "BANNED") {
          setBanMessage(data.reason || "You have been banned.");
        }
      } catch (e) {
        console.error("Failed to parse WS message", e);
      }
    };

    ws.onclose = (e) => {
      console.log("WS Closed");
      setConnected(false);
      if (e.code === 4003 || e.code === 1008) {
        setBanMessage(e.reason || "You have been banned from this room.");
      } else {
        // Try to reconnect if not banned
        setTimeout(() => {
          setRetryCount((c) => c + 1);
        }, 2000);
      }
    };

    return () => {
      ws.onclose = null;
      ws.close();
    };
  }, [roomId, retryCount, password]); // Retry on password change or disconnect

  const send = (msg: unknown) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  };

  const saveName = (newName: string) => {
    const trimmed = newName.trim().slice(0, 20);
    if (!trimmed) return;
    setMyName(trimmed);
    localStorage.setItem("quiz_name", trimmed);
    send({ type: "UPDATE_PLAYER_PROFILE", params: { name: trimmed } });
    setIsEditingName(false);
  };

  const startGame = () => send({ type: "START_GAME" });
  const buzz = () => send({ type: "BUZZ" });
  const submitAnswer = () => send({ type: "ANSWER", answer: localAnswer });
  const vote = (isCorrect: boolean | null) => send({ type: "VOTE", isCorrect });
  const giveUp = () => send({ type: "GIVE_UP" });
  const nextQuestion = () => send({ type: "NEXT_QUESTION" });
  const stopGame = () => send({ type: "STOP_GAME" }); // Not impl on server yet but reserved

  const activePlayers = roomState?.players || [];
  const myPlayer = activePlayers.find((p) => p.id === myId);
  const showStartButton = roomState?.state === "WAITING" && myPlayer?.isHost;
  const myQueueIndex = myId && roomState
    ? roomState.buzzerQueue.indexOf(myId)
    : -1;
  const amIActiveHead = myQueueIndex === 0;
  const hasGivenUp = myId && roomState
    ? roomState.gaveUpPlayerIds.includes(myId)
    : false;

  // [Refinement] Auto-focus answer input when it's my turn
  useEffect(() => {
    if (amIActiveHead && roomState?.state === "ANSWERING") {
      inputRef.current?.focus();
    }
  }, [amIActiveHead, roomState?.state]);

  // Render Logic
  if (roomNotFound) {
    const info = typeof roomNotFound === "object"
      ? roomNotFound
      : { found: false, isReserved: false, isOwner: false };
    const canCreate = user && !user.isAnonymous;

    return (
      <QuizViewNotFound
        roomId={roomId}
        info={info as { found: false; isReserved?: boolean; isOwner?: boolean }}
        canCreate={!!canCreate}
        onCreate={async () => {
          try {
            const res = await fetch(`/api/quiz/rooms`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                customId: roomId,
                creatorId: myId,
              }),
            });
            if (res.ok) {
              setRoomNotFound(false);
              setRetryCount((prev) => prev + 1);
            }
          } catch (err) {
            console.error("Failed to create room", err);
          }
        }}
      />
    );
  }

  if (passwordError) {
    return (
      <QuizViewLocked
        roomId={roomId}
        inputPassword={inputPassword}
        setInputPassword={setInputPassword}
        onSubmit={() => {
          const pw = inputPassword.trim();
          if (pw) {
            setPassword(pw);
            localStorage.setItem(`quiz_pw_${roomId}`, pw);
            setPasswordError(null);
            setRetryCount((c) => c + 1);
          }
        }}
      />
    );
  }

  if (banMessage) {
    return <QuizViewBanned message={banMessage} />;
  }

  if (!connected || !roomState) {
    return (
      <div class="p-10 text-center text-primary animate-pulse font-black uppercase tracking-widest">
        Connecting to Room...
      </div>
    );
  }

  return (
    <div class="flex h-screen bg-base-100 overflow-hidden">
      {/* Sidebar */}
      <QuizSidebar
        sidebarCollapsed={sidebarCollapsed}
        showCode={showCode}
        setShowCode={setShowCode}
        roomId={roomId}
        showQuitDialog={showQuitDialog}
        setShowQuitDialog={setShowQuitDialog}
        roomState={roomState as unknown as RoomState}
        myPlayerId={myId}
        myPlayer={myPlayer}
        stopGame={stopGame}
        contextMenu={contextMenu}
        setContextMenu={setContextMenu}
        isEditingName={isEditingName}
        setIsEditingName={setIsEditingName}
        tempName={tempName}
        setTempName={setTempName}
        saveName={saveName}
        myName={myName}
        setShowSettings={setShowSettings}
      />

      {/* Main Content */}
      <div class="flex-1 flex flex-col relative h-full overflow-hidden bg-base-200/50">
        {/* Collapse Toggle */}
        <button
          type="button"
          class="absolute top-4 left-4 z-20 btn btn-circle btn-sm btn-ghost bg-base-100 shadow-sm"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          {sidebarCollapsed
            ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            )
            : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
              </svg>
            )}
        </button>

        <div class="flex-1 overflow-y-auto p-4 md:p-8">
          <div class="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <header class="flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
              <div>
                <h1 class="text-4xl font-black italic tracking-tighter text-primary">
                  THE FARM QUIZ
                </h1>
                <p class="text-xs uppercase font-bold opacity-30 tracking-widest">
                  Real-time Trivia Battle
                </p>
              </div>
              <div class="flex items-center gap-4">
                {roomState.currentPoints > 0 && roomState.state !== "WAITING" &&
                  (
                    <div class="flex flex-col items-center">
                      <span class="text-[10px] bg-secondary text-secondary-content px-2 rounded font-black uppercase">
                        Points
                      </span>
                      <span class="text-3xl font-black text-secondary">
                        {roomState.currentPoints}
                      </span>
                    </div>
                  )}
                {roomState.state !== "WAITING" && (
                  <div class="flex flex-col items-center min-w-[60px]">
                    <span class="text-[10px] bg-neutral text-neutral-content px-2 rounded font-black uppercase">
                      Timer
                    </span>
                    <span
                      class={`text-3xl font-black font-mono ${
                        roomState.timeRemaining <= 5
                          ? "text-error animate-pulse"
                          : "opacity-60"
                      }`}
                    >
                      {roomState.timeRemaining}s
                    </span>
                  </div>
                )}
              </div>
            </header>

            {roomState.state === "WAITING" && (
              <div class="flex flex-col items-center justify-center min-h-[400px]">
                {showStartButton
                  ? (
                    <button
                      type="button"
                      class="btn btn-primary btn-lg text-2xl px-12 shadow-lg hover:scale-105 transition-transform"
                      onClick={startGame}
                    >
                      Start Game
                    </button>
                  )
                  : (
                    <div class="flex flex-col items-center gap-4">
                      <span class="loading loading-spinner loading-lg text-primary">
                      </span>
                      <span class="text-sm opacity-70">
                        Waiting for host to start...
                      </span>
                    </div>
                  )}
              </div>
            )}

            {roomState.state !== "WAITING" && (
              <div class="card bg-base-100 shadow-2xl border border-base-300 min-h-[400px]">
                <div class="card-body items-center text-center">
                  {(roomState.state === "QUESTION_STREAMING" ||
                    roomState.state === "QUESTION_WAITING" ||
                    roomState.state === "ANSWERING" ||
                    roomState.state === "VOTING") && (
                    <div class="w-full space-y-8 animate-in fade-in duration-300">
                      <div class="space-y-4">
                        <div class="flex justify-between items-end border-b-4 border-base-200 pb-2">
                          <div class="flex items-center gap-2">
                            <span class="badge badge-outline badge-sm font-mono">
                              {(roomState.currentQuestionIndex ?? 0) +
                                1}/{roomState.totalQuestions ?? "?"}
                            </span>
                            <span class="text-sm font-black uppercase text-primary opacity-50">
                              Question
                            </span>
                          </div>
                          {roomState.state === "QUESTION_WAITING" && (
                            <span class="badge badge-warning font-black animate-pulse">
                              BUZZ NOW!
                            </span>
                          )}
                        </div>
                        <h2 class="text-3xl md:text-5xl font-black leading-tight tracking-tight">
                          {roomState.currentQuestion}
                          {roomState.state === "QUESTION_STREAMING" && (
                            <span class="inline-block w-4 h-4 bg-primary ml-2 animate-pulse rounded-full">
                            </span>
                          )}
                        </h2>

                        {/* Action Zone */}
                        {(roomState.state !== "VOTING" &&
                          !activePlayers.find((p) =>
                            p.id === roomState.currentBuzzer
                          )) && !hasGivenUp && (
                          <div class="py-6 flex justify-center">
                            {myQueueIndex < 0
                              ? (
                                <button
                                  type="button"
                                  onClick={buzz}
                                  disabled={
                                    roomState.state ===
                                      "QUESTION_STREAMING" &&
                                    false /* Can shadow buzz? Yes */
                                  }
                                  class={`group relative w-48 h-48 rounded-full border-8 shadow-2xl transition-all duration-200 active:scale-95 flex flex-col items-center justify-center
                                                        ${
                                    roomState.state === "QUESTION_WAITING" ||
                                      roomState.state === "QUESTION_STREAMING"
                                      ? "border-error bg-error/10 hover:bg-error/20 text-error cursor-pointer"
                                      : "border-base-300 bg-base-200 opacity-50 cursor-not-allowed"
                                  }
                                                    `}
                                >
                                  <span class="text-4xl font-black group-hover:scale-110 transition-transform">
                                    BUZZ
                                  </span>
                                  <span class="text-[10px] font-black uppercase tracking-widest opacity-60 mt-1">
                                    {roomState.state === "QUESTION_STREAMING"
                                      ? "Or wait..."
                                      : "NOW!"}
                                  </span>
                                </button>
                              )
                              : (
                                <div class="alert alert-info max-w-sm shadow-xl border-4 border-info">
                                  <div class="flex flex-col items-center w-full">
                                    <span class="text-4xl font-black mb-2">
                                      #{myQueueIndex + 1}
                                    </span>
                                    <span class="text-xs font-bold uppercase opacity-80">
                                      In Queue
                                    </span>
                                  </div>
                                </div>
                              )}
                          </div>
                        )}

                        {/* Active Answering UI */}
                        {(roomState.state === "ANSWERING" ||
                          roomState.state === "VOTING") && (
                          <div class="bg-base-200 rounded-3xl p-8 border-4 border-base-300 animate-in zoom-in duration-300">
                            <div class="flex flex-col items-center gap-4">
                              <div class="badge badge-lg badge-neutral font-mono shadow-md">
                                {(() => {
                                  const buzzerPlayer = activePlayers.find((p) =>
                                    p.id === roomState.buzzerQueue?.[0]
                                  );
                                  return buzzerPlayer?.name || myPlayer?.name ||
                                    "Player";
                                })()}
                                {roomState.state === "ANSWERING"
                                  ? " is answering..."
                                  : " answered:"}
                              </div>

                              {roomState.currentAnswer && (
                                <div class="text-4xl md:text-6xl font-black text-accent italic p-4 bg-base-100 rounded-xl w-full wrap-break-word shadow-inner">
                                  "{roomState.currentAnswer}"
                                </div>
                              )}
                            </div>

                            {/* Voting Controls */}
                            {roomState.state === "VOTING" && (
                              <div class="mt-8 space-y-4">
                                <div class="flex gap-2 justify-center">
                                  <button
                                    type="button"
                                    onClick={() => vote(true)}
                                    class="btn btn-success btn-lg flex-1 font-black max-w-[120px] shadow-lg hover:scale-105"
                                  >
                                    YES ✓
                                  </button>
                                  {!roomState.correctAnswer && (
                                    <button
                                      type="button"
                                      onClick={() => vote(null)}
                                      class="btn btn-ghost btn-lg flex-1 font-black max-w-[120px] opacity-50"
                                    >
                                      SKIP
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => vote(false)}
                                    class="btn btn-error btn-lg flex-1 font-black max-w-[120px] shadow-lg hover:scale-105"
                                  >
                                    NO ✗
                                  </button>
                                </div>
                                <p class="text-[10px] uppercase font-bold opacity-40">
                                  Vote on the accuracy of the answer
                                </p>

                                {roomState.correctAnswer && (
                                  <div class="alert alert-success mt-4 shadow-xl">
                                    <div>
                                      <div class="font-bold text-xs uppercase opacity-70">
                                        Answer Revealed
                                      </div>
                                      <div class="font-black text-xl">
                                        {roomState.correctAnswer}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Answering Input (If it's me) */}
                            {amIActiveHead && roomState.state === "ANSWERING" &&
                              (
                                <div class="mt-8 space-y-4 animate-bounce-in">
                                  <div class="label">
                                    <span class="label-text font-black uppercase text-primary w-full text-center">
                                      It's your turn!
                                    </span>
                                  </div>
                                  <div class="join w-full max-w-md mx-auto shadow-xl">
                                    <input
                                      ref={inputRef}
                                      type="text"
                                      class="input input-lg input-primary join-item w-full font-bold text-center"
                                      placeholder="Type answer..."
                                      autoFocus
                                      value={localAnswer}
                                      onInput={(e) =>
                                        setLocalAnswer(
                                          (e.target as HTMLInputElement).value,
                                        )}
                                      onKeyDown={(e) =>
                                        e.key === "Enter" && submitAnswer()}
                                    />
                                    <button
                                      type="button"
                                      class="btn btn-primary btn-lg join-item"
                                      onClick={submitAnswer}
                                    >
                                      SUBMIT
                                    </button>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={giveUp}
                                    class="btn btn-ghost btn-xs text-error opacity-50 hover:opacity-100"
                                  >
                                    I don't know (Pass)
                                  </button>
                                </div>
                              )}
                          </div>
                        )}

                        {hasGivenUp && !amIActiveHead && (
                          <div class="alert max-w-xs mx-auto opacity-50">
                            <span class="text-2xl">⚐</span>
                            <span class="font-bold text-xs uppercase">
                              You passed this round
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {(roomState.state === "REVEAL") && (
                    <div class="space-y-6 py-8 w-full animate-in fade-in slide-in-from-bottom duration-500">
                      <div>
                        <h2 class="text-xl font-bold opacity-50 mb-2">
                          Question
                        </h2>
                        <p class="text-2xl font-serif italic opacity-80">
                          {roomState.currentQuestion}
                        </p>
                      </div>
                      <div class="card bg-success text-success-content p-8 shadow-2xl border-4 border-white/20 transform hover:scale-105 transition-transform duration-300">
                        <p class="text-xs uppercase font-black tracking-widest opacity-60 mb-2">
                          Correct Answer
                        </p>
                        <p class="text-5xl md:text-6xl font-black tracking-tight">
                          {roomState.correctAnswer}
                        </p>
                      </div>

                      {myPlayer?.isHost
                        ? (
                          <button
                            type="button"
                            onClick={nextQuestion}
                            class="btn btn-primary btn-lg shadow-xl font-black uppercase tracking-widest px-12 h-20 text-xl"
                          >
                            Next Question →
                          </button>
                        )
                        : (
                          <div class="animate-pulse font-bold opacity-50">
                            Waiting for next question...
                          </div>
                        )}
                    </div>
                  )}

                  {(roomState.state === "SCOREBOARD" ||
                    roomState.state === "FINISHED") && (
                    <div class="space-y-8 py-12 w-full">
                      <h2 class="text-6xl font-black text-primary italic tracking-tighter drop-shadow-sm">
                        GAME OVER
                      </h2>

                      <div class="card bg-base-200 p-6 shadow-inner border border-base-300">
                        <h3 class="font-black uppercase tracking-widest opacity-40 mb-4">
                          Final Standings
                        </h3>
                        <div class="space-y-2">
                          {[...activePlayers].sort((a, b) => b.score - a.score)
                            .slice(0, 3).map((p, i) => (
                              <div
                                key={p.id}
                                class={`flex items-center justify-between p-4 rounded-xl ${
                                  i === 0
                                    ? "bg-warning text-warning-content font-black shadow-lg scale-105"
                                    : "bg-base-100"
                                }`}
                              >
                                <div class="flex items-center gap-4">
                                  <span class="text-2xl opacity-50 w-8">
                                    #{i + 1}
                                  </span>
                                  <span class="text-xl">{p.name}</span>
                                </div>
                                <span class="text-2xl font-mono">
                                  {p.score}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>

                      {myPlayer?.isHost
                        ? (
                          <button
                            type="button"
                            onClick={startGame}
                            class="btn btn-outline btn-wide btn-lg font-black uppercase tracking-widest border-4"
                          >
                            Play Again
                          </button>
                        )
                        : (
                          <div class="opacity-50 font-bold">
                            Waiting for host to restart...
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals & Overlays */}
      <QuizQuitModal
        isOpen={showQuitDialog}
        onClose={() => setShowQuitDialog(false)}
        onQuit={() => {
          send({ type: "LEAVE_ROOM" });
          wsRef.current?.close();
          globalThis.location.href = "/quiz";
        }}
      />

      {contextMenu && (
        <QuizPlayerMenu
          x={contextMenu.x}
          y={contextMenu.y}
          playerId={contextMenu.playerId}
          onClose={() => setContextMenu(null)}
          // Only show kick/ban if I am authorized
          onKick={(roomState.ownerId === myId || myPlayer?.isHost)
            ? (id) => send({ type: "KICK_PLAYER", playerId: id })
            : undefined}
          onBan={(roomState.ownerId === myId || myPlayer?.isHost)
            ? (id) =>
              setConfirmModal({
                isOpen: true,
                title: "Ban User",
                message:
                  "Banning a user will prevent them from rejoining this room via IP.",
                onConfirm: () =>
                  send({
                    type: "BAN_PLAYER",
                    playerId: id,
                    banIP: true,
                    banUser: true,
                  }),
              })
            : undefined}
        />
      )}

      {confirmModal && (
        <div class="modal modal-open z-50">
          <div class="modal-box">
            <h3 class="font-bold text-lg">{confirmModal.title}</h3>
            <p class="py-4 opacity-70">{confirmModal.message}</p>
            <div class="modal-action">
              <button
                type="button"
                class="btn"
                onClick={() => setConfirmModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-error"
                onClick={() => {
                  confirmModal.onConfirm();
                  setConfirmModal(null);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettings && roomState.settings && (
        <dialog open class="modal modal-open z-50">
          <div class="modal-box max-w-lg">
            <div class="flex justify-between items-center mb-6">
              <h3 class="font-bold text-2xl uppercase tracking-tighter">
                Room Settings
              </h3>
              <button
                type="button"
                class="btn btn-sm btn-circle btn-ghost"
                onClick={() =>
                  setShowSettings(false)}
              >
                ✕
              </button>
            </div>

            <div class="space-y-6">
              <div class="form-control">
                <label class="label cursor-pointer justify-start gap-4 p-4 border rounded-xl hover:bg-base-200 transition-colors">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-primary"
                    checked={roomState.settings.isPublic}
                    onChange={(e) =>
                      send({
                        type: "UPDATE_SETTINGS",
                        settings: {
                          isPublic: (e.target as HTMLInputElement).checked,
                        },
                      })}
                  />
                  <div>
                    <span class="label-text font-bold block">Public Room</span>
                    <span class="label-text-alt opacity-60">
                      Visible in main lobby list
                    </span>
                  </div>
                </label>
              </div>

              <div class="form-control">
                <label class="label">
                  <span class="label-text font-bold">Question Set</span>
                </label>
                <select
                  class="select select-bordered w-full"
                  value={roomState.settings.selectedSetId || ""}
                  onChange={(e) => {
                    const value = (e.target as HTMLSelectElement).value;
                    send({
                      type: "UPDATE_SETTINGS",
                      settings: {
                        selectedSetId: value || undefined,
                      },
                    });
                  }}
                  onFocus={async () => {
                    // Fetch sets when dropdown is focused
                    try {
                      const res = await fetch("/api/quiz/sets");
                      if (res.ok) {
                        const data = await res.json();
                        setAvailableSets(data);
                      }
                    } catch {
                      // Ignore fetch errors
                    }
                  }}
                >
                  <option value="">Default Questions</option>
                  {availableSets.my.length > 0 && (
                    <optgroup label="My Sets">
                      {availableSets.my.map((s: any) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </optgroup>
                  )}
                  {availableSets.public.length > 0 && (
                    <optgroup label="Public Sets">
                      {availableSets.public.map((s: any) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <label class="label">
                  <span class="label-text-alt opacity-60">
                    Select a question set or use defaults
                  </span>
                </label>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-bold">Answering Time (s)</span>
                  </label>
                  <input
                    type="number"
                    class="input input-bordered w-full"
                    min="5"
                    max="60"
                    value={roomState.settings.answeringTime}
                    onChange={(e) =>
                      send({
                        type: "UPDATE_SETTINGS",
                        settings: {
                          answeringTime: parseInt(
                            (e.target as HTMLInputElement).value,
                          ),
                        },
                      })}
                  />
                </div>
                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-bold">Points / Question</span>
                  </label>
                  <input
                    type="number"
                    class="input input-bordered w-full"
                    min="1"
                    max="1000"
                    value={roomState.settings.pointsPerQuestion}
                    onChange={(e) =>
                      send({
                        type: "UPDATE_SETTINGS",
                        settings: {
                          pointsPerQuestion: parseInt(
                            (e.target as HTMLInputElement).value,
                          ),
                        },
                      })}
                  />
                </div>
              </div>

              <div class="divider text-xs font-black opacity-20 uppercase tracking-widest">
                Security
              </div>
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-bold">Room Password</span>
                </label>
                <div class="join w-full">
                  <input
                    type="text"
                    class="input input-bordered w-full join-item font-mono"
                    placeholder="No password set"
                    value={roomState.settings.password || ""}
                    onChange={(e) =>
                      send({
                        type: "UPDATE_SETTINGS",
                        settings: {
                          password: (e.target as HTMLInputElement).value,
                        },
                      })}
                  />
                  {roomState.settings.password && (
                    <button
                      type="button"
                      class="btn btn-ghost join-item text-error"
                      onClick={() =>
                        send({
                          type: "UPDATE_SETTINGS",
                          settings: { password: "" },
                        })}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <label class="label">
                  <span class="label-text-alt opacity-50">
                    Leave empty to remove password protection.
                  </span>
                </label>
              </div>
            </div>

            <div class="modal-action">
              <button
                type="button"
                class="btn"
                onClick={() =>
                  setShowSettings(false)}
              >
                Close
              </button>
            </div>
          </div>
          <div
            class="modal-backdrop"
            onClick={() => setShowSettings(false)}
          >
          </div>
        </dialog>
      )}
    </div>
  );
};
